/* eslint-disable react/jsx-props-no-spreading */
import React, { Component, createRef } from 'react';
import { connect } from 'react-redux';
import { array, arrayOf, bool, func, number, object, oneOf, shape, string } from 'prop-types';
import { get, isEmpty, isEqual } from 'lodash';
import classNames from 'classnames/bind';
import FeatureFlag from 'yoda-core-components/lib/components/FeatureFlag/FeatureFlag';
import LocalStorage from 'yoda-core-components/lib/helpers/LocalStorage/LocalStorage';
import PointCross from 'yoda-core-components/lib/components/PointCross/PointCross';
import Rating from 'yoda-core-components/lib/components/Rating';
import S7Image from 'yoda-core-components/lib/components/S7Image/S7Image';
import Utils from 'yoda-core-components/lib/helpers/Utils/Utils';
import ErrorBoundaryConnected from '../ErrorBoundary/ErrorBoundary';
import QuickAddToCartConnected from '../QuickAddTocart/QuickAddToCart';
import SaveForLaterConnected from '../SaveForLater/SaveForLater';
import SlickCarousel from '../SlickCarousel/SlickCarousel';
import * as styles from './ProductCard.css';
import productCardConfig from './ProductCard.config';
import EcoRebates from './components/EcoRebates/EcoRebates';
import ExtendedSize from './components/ExtendedSize/ExtendedSize';
import ProductAvailabilityConnected from './components/ProductAvailability/ProductAvailability';
import ProductBadge from './components/ProductBadge/ProductBadge';
import ProductImage from './components/ProductImage/ProductImage';
import ProductPrice from './components/ProductPrice/ProductPrice';
import ProductTitle from './components/ProductTitle/ProductTitle';
import ProductVideo from './components/ProductVideo/ProductVideo';
import Swatches from './components/Swatches';

const cx = classNames.bind(styles);

const LAZY_LOAD_SCROLL_OFFSET = {
    8: 500,
    12: 1000,
    16: 1500,
};

export class ProductCard extends Component {
    static propTypes = {
        deviceType: object.isRequired,
        featureFlags: object.isRequired,
        preferences: object.isRequired,
        altImageUrl: string,
        badge: object,
        bottomBadge: object,
        cardCustomClass: string,
        cardTheme: oneOf([
            productCardConfig.cardTheme.CARTRIDGE,
            productCardConfig.cardTheme.GALLERY,
            productCardConfig.cardTheme.ONE_RECORD_BANNER,
        ]),
        cardType: oneOf([productCardConfig.cardTypes.GRID, productCardConfig.cardTypes.LIST]),
        customClass: string,
        defaultOffSet: number,
        ecoRebate: bool,
        enableMiniPDP: bool,
        enableProductTitlePrefix: bool,
        imageUrl: string,
        index: number,
        isAddToCartClick: bool,
        isCouponRecommendationsModal: bool,
        isCriteoProduct: bool,
        isEnsembledProduct: bool,
        lazyLoadImages: bool,
        noImageFoundSrcDefault: string,
        onAddToCartClick: func,
        onImageClick: func,
        onProductClick: func,
        ppId: string,
        pricingConfig: object,
        productList: shape({ products: array }),
        productUrl: string,
        shallowCard: bool,
        showPaneLoader: bool,
        showPricing: bool,
        showSaveForlater: bool,
        skuId: string,
        skuSwatch: array,
        storeAvailabilityFilter: arrayOf(object),
        title: string,
        upperBadge: object,
        videoId: string,
    };

    /** @properties {Default set up} [description] */
    static defaultProps = {
        altImageUrl: '',
        badge: null,
        bottomBadge: {},
        cardCustomClass: '',
        cardTheme: productCardConfig.cardTheme.GALLERY,
        cardType: productCardConfig.cardTypes.GRID,
        customClass: '',
        defaultOffSet: 500,
        ecoRebate: false,
        enableMiniPDP: false,
        enableProductTitlePrefix: false,
        imageUrl: '',
        index: 0,
        isAddToCartClick: false,
        isCouponRecommendationsModal: false,
        isCriteoProduct: false,
        isEnsembledProduct: false,
        lazyLoadImages: false,
        noImageFoundSrcDefault: 'https://m.jcpenney.com/b/assets/img/noimage.jpeg',
        onAddToCartClick: () => {},
        onImageClick: () => {},
        onProductClick: () => {},
        ppId: '',
        pricingConfig: {},
        productList: { products: [] },
        productUrl: '',
        shallowCard: false,
        showPaneLoader: false,
        showPricing: true,
        showSaveForlater: false,
        skuId: '',
        skuSwatch: [],
        storeAvailabilityFilter: [],
        title: '',
        upperBadge: {},
        videoId: '',
    };

    constructor(props) {
        super();
        this.state = {
            activeSwatchImageId: '',
            altImageUrl: props.altImageUrl,
            defaultImageFail: false,
            imageError: false,
            imageSrc: '',
            noAltImage: false,
            openRebatesModal: false,
            playVideo: false,
            productCardWidth: 0,
            swatchImageSrc: '',
            swatchSkuId: null,
        };

        this.productCardRef = createRef();
        this.defaultOffSet = props.defaultOffSet;
        this.lazyLoadGalleryCardsNumber = get(props.preferences, 'lazyLoadGalleryCardsNumber', 8);
        this.lazyLoadNextGalleryCards = get(props.featureFlags, 'lazyLoadNextGalleryCards', false);

        this.lazyLoadGalleryCardsScrollOffset =
            this.defaultOffSet > LAZY_LOAD_SCROLL_OFFSET[this.lazyLoadGalleryCardsNumber]
                ? this.defaultOffSet
                : LAZY_LOAD_SCROLL_OFFSET[this.lazyLoadGalleryCardsNumber];

        this.pointCrossOffsetProps = this.lazyLoadNextGalleryCards
            ? { offset: this.lazyLoadGalleryCardsScrollOffset }
            : {};
    }

    /* istanbul ignore next */
    componentDidMount() {
        if (this.productCardRef && this.productCardRef.current) {
            this.setState({ productCardWidth: this.productCardRef.current.clientWidth });
        }
        window.addEventListener('resize', () => {
            if (this.productCardRef && this.productCardRef.current) {
                this.setState({ productCardWidth: this.productCardRef.current.clientWidth });
            }
        });
    }

    shouldComponentUpdate(nextProps, nextState) {
        return !isEqual(nextProps, this.props) || !isEqual(nextState, this.state);
    }

    /* istanbul ignore next */
    componentWillUnmount() {
        window.removeEventListener('resize', () => {
            if (this.productCardRef && this.productCardRef.current) {
                this.setState({ productCardWidth: this.productCardRef.current.clientWidth });
            }
        });
    }

    /* istanbul ignore next */
    handleSwatchDesktopEvent = (event, currentSwatch) => {
        event.preventDefault();

        const {
            props: {
                featureFlags: { disableEnsembledImage = false },
                isEnsembledProduct = false,
            },
            state: { swatchClicked = false },
        } = this;

        const getScene7ImageUrl = (imageId) =>
            (imageId &&
                `//s7d4.scene7.com/is/image/JCPenney/${imageId}?wid=350&hei=350&op_usm=.4,.8,0,0&resmode=sharp2`) ||
            '';

        if (!isEmpty(currentSwatch)) {
            const { skuAltImages: [{ imgId = '' } = {}] = [] } = currentSwatch;
            const altImageUrl = getScene7ImageUrl(imgId);
            const imageUrl = getScene7ImageUrl(currentSwatch.colorizedImageId);

            const imageData = {
                imageError: false,
                imageSrc: imageUrl,
                swatchImageSrc: imageUrl,
                swatchSkuId: currentSwatch.skuId,
            };

            if (!disableEnsembledImage)
                imageData.altImageUrl = isEnsembledProduct ? altImageUrl || imageUrl : altImageUrl;

            if (event.type === 'click') {
                imageData.activeSwatchImageId = currentSwatch.swatchImageId;
                imageData.swatchClicked = true;
                imageData.defaultSwatchData = { ...imageData };
                LocalStorage.setData('selectedSwatchId', currentSwatch.skuId);
            }

            if (event.type !== 'click' && !imageData.activeSwatchImageId && !swatchClicked)
                imageData.activeSwatchImageId = currentSwatch.swatchImageId;

            this.setState(imageData);
        }
    };

    // for Mobile and Tablet redirect to PDP
    /* istanbul ignore next */
    handleSwatchMobileEvent = ({ skuId }) => {
        const { productUrl } = this.props;

        if (skuId) {
            const getFacetSelectedValue = (url) => {
                const facetSelected = Utils.getURLParameterBykey('facetSelected', url);

                if (isEmpty(facetSelected)) {
                    return 'color';
                }

                const facetSelectedSplit = facetSelected.split(',');
                const hasColorFacet = facetSelectedSplit.includes('color');

                return hasColorFacet ? facetSelected : facetSelectedSplit.concat('color').join();
            };

            if (productUrl) {
                const updatedProductUrl = Utils.updateQueryStringParameter(
                    productUrl,
                    'selectedSKUId',
                    skuId
                );

                LocalStorage.setData('fromGallery', { scrollTo: 'swatch' });
                this.setGalleryScrollInLocalStorage();
                window.location.assign(
                    Utils.updateQueryStringParameter(
                        updatedProductUrl,
                        'facetSelected',
                        getFacetSelectedValue(updatedProductUrl)
                    )
                );
            }
        }
    };

    /* istanbul ignore next */
    setGalleryScrollInLocalStorage = () => {
        const { ppId } = this.props;
        const topOfElement = !__SERVER__ ? window.pageYOffset : 1000;
        LocalStorage.setData('galleryScrollTo', ppId);
        LocalStorage.setData('galleryScrollToOffSet', topOfElement);
    };

    /* istanbul ignore next */
    setParentState = (newState) => {
        this.setState(newState);
    };

    getProductVideo = (additionalProps) => {
        const { playVideo } = this.state;
        const { videoId } = this.props;
        return videoId ? (
            <ProductVideo
                {...this.props}
                playVideo={playVideo}
                setParentState={this.setParentState}
                {...additionalProps}
            />
        ) : null;
    };

    getExtendedSize = (additionalProps) => {
        const { bottomBadge } = this.props;
        return bottomBadge && bottomBadge.text ? (
            <ExtendedSize {...this.props} {...additionalProps} />
        ) : null;
    };

    getProductBadge = () => {
        const { badge, upperBadge } = this.props;
        const { playVideo } = this.state;
        const activeBadge = badge || upperBadge;

        return activeBadge && activeBadge.text ? (
            <ProductBadge {...this.props} playVideo={playVideo} />
        ) : null;
    };

    /* istanbul ignore next */
    getFacetSelectedValue = (productUrl) => {
        let facetSelectedValue;
        const getfacetSelectedValueUrl = Utils.getURLParameterBykey('facetSelected', productUrl);
        if (isEmpty(getfacetSelectedValueUrl)) {
            facetSelectedValue = 'color';
        } else {
            const getModifiedfacetSelectedValue = getfacetSelectedValueUrl.split(',');
            const colorFacetFound = getModifiedfacetSelectedValue.includes('color');
            if (colorFacetFound) {
                facetSelectedValue = getfacetSelectedValueUrl;
            } else {
                facetSelectedValue = getModifiedfacetSelectedValue.concat('color').join();
            }
        }
        return facetSelectedValue;
    };

    /**
     * display default image for ensembled products
     * when user hovers out of img/alt-image/swatches
     */
    /* istanbul ignore next */
    resetSelectionForEnsembleHover = () => {
        const { defaultSwatchData } = this.state;
        const { isEnsembledProduct } = this.props;

        if (isEnsembledProduct) {
            if (defaultSwatchData) {
                this.setState({ ...defaultSwatchData });
            } else {
                const imageData = {
                    swatchImageSrc: '',
                    imageSrc: '',
                    imageError: false,
                    swatchSkuId: '',
                    activeSwatchImageId: '',
                };

                this.setState(imageData);
            }
        }
    };

    constructProductImagesAndVideos = () => {
        const { title, imageUrl, altImageUrl } = this.props;
        let displayUrl = imageUrl;
        const productImagesAndVideos = [];
        const matchImage = /scene7.com(.+)/g.test(displayUrl);
        // Image optimization is not required for Gallery Product card
        if (matchImage) {
            displayUrl =
                displayUrl.indexOf('?') > 0
                    ? `${displayUrl}&wid=170&hei=170&op_sharpen=1`
                    : `${displayUrl}?wid=170&hei=170&op_sharpen=1`;
        }
        const imageobj = {
            altText: title,
            displayText: title,
            key: displayUrl,
            type: 'PRIMARY',
            url: displayUrl,
        };
        let displayAltImgUrl = altImageUrl;

        const matchAltImage = /scene7.com(.+)/g.test(displayAltImgUrl);
        // Image optimization is not required for Gallery Product card
        if (matchAltImage) {
            displayAltImgUrl =
                displayAltImgUrl.indexOf('?') > 0
                    ? `${displayAltImgUrl}&wid=170&hei=170&op_sharpen=1`
                    : `${displayAltImgUrl}?wid=170&hei=170&op_sharpen=1`;
        }
        const altImageobj = {
            altText: title,
            key: displayAltImgUrl,
            swatchUrl: displayAltImgUrl,
            type: 'ALTERNATE',
            url: displayAltImgUrl,
        };

        productImagesAndVideos.push(imageobj);
        altImageUrl && productImagesAndVideos.push(altImageobj);
        return productImagesAndVideos;
    };

    renderProductImage = () => {
        const {
            pointCrossOffsetProps,
            props,
            props: {
                altImageUrl: defaultAltImageUrl,
                cardTheme,
                featureFlags: { disableSkuAltImgeForNoImage, enableProductSliding = false } = {},
                productUrl,
            } = {},
            state: {
                altImageUrl: swatchAltImage,
                imageError,
                imageSrc,
                swatchImageSrc,
                swatchSkuId,
            } = {},
        } = this;

        const propsClone = { ...props };

        if (swatchSkuId) {
            const productUrlUpdated = Utils.updateQueryStringParameter(
                productUrl,
                'selectedSKUId',
                swatchSkuId
            );

            const productUpdateFacetUrl = Utils.updateQueryStringParameter(
                productUrlUpdated,
                'facetSelected',
                this.getFacetSelectedValue(productUrlUpdated)
            );

            propsClone.productUrl = productUpdateFacetUrl;
        }

        const altImageUrl = disableSkuAltImgeForNoImage
            ? swatchAltImage || defaultAltImageUrl
            : swatchAltImage;

        const enableProductImage =
            !enableProductSliding || cardTheme !== productCardConfig.cardTheme.GALLERY;

        const productImageProps = {
            ...propsClone,
            altImageUrl,
            imageError,
            imageSrc,
            pointCrossOffsetProps,
            swatchImageSrc,
        };

        return enableProductImage && <ProductImage {...productImageProps} />;
    };

    renderProductSliding = () => {
        const {
            cardTheme,
            featureFlags: { enableProductSliding = false },
            lazyLoadImages,
            noImageFoundSrcDefault,
            onImageClick,
            preferences: { noImageFoundSrc = '' },
            productUrl,
        } = this.props;

        // all below changes are for SlickCarousel
        const productImagesAndVideos =
            cardTheme === productCardConfig.cardTheme.GALLERY &&
            this.constructProductImagesAndVideos();

        const carouselTheme = {
            carouselSelected: cx(styles.selected),
            carouselBullets: cx(styles.carouselBullets),
            bullet: styles.bullet,
            slideClass: styles.carouselSlide,
            bulletContainer: cx(styles.bulletContainer),
        };
        // need to set this key to force re-render on client
        const key = `${productImagesAndVideos.length}-${this.state.hasMounted ? 'c' : 's'}`;
        const displayProductSliding = enableProductSliding && productImagesAndVideos.length > 0; // as product card will be common component added to make sure it will load only for gallery not for RR zone

        const productImageListRenderer = (dataItem, idx) => {
            const noImageSrc = noImageFoundSrc || noImageFoundSrcDefault;

            let productUpdatedUrl = productUrl;
            const { swatchSkuId } = this.state;

            /* istanbul ignore next */
            if (swatchSkuId) {
                productUpdatedUrl = Utils.updateQueryStringParameter(
                    productUrl,
                    'selectedSKUId',
                    swatchSkuId
                );
            }

            /* istanbul ignore next */
            const image = (
                <a
                    href={productUpdatedUrl}
                    onClick={(e) => {
                        onImageClick(e, 'image');
                    }}
                >
                    <ErrorBoundaryConnected>
                        <S7Image
                            src={dataItem.url}
                            alt={dataItem.altText}
                            automationId={`slick-1-s7image-${idx}`}
                            imageErrorSrc={noImageSrc}
                        />
                    </ErrorBoundaryConnected>
                </a>
            );

            return lazyLoadImages ? (
                <PointCross lazyLoadComponent {...this.pointCrossOffsetProps}>
                    {image}
                </PointCross>
            ) : (
                image
            );
        };

        return displayProductSliding ? (
            <ErrorBoundaryConnected>
                <SlickCarousel
                    carouselData={productImagesAndVideos}
                    carouselItemRenderer={productImageListRenderer}
                    themeConfig={carouselTheme}
                    automationId="data-slick-carousel"
                    rewindOnChange
                    autoSize
                    lazyLoad
                    displayArrows={false}
                    repeat={false}
                    key={key}
                />
            </ErrorBoundaryConnected>
        ) : null;
    };

    renderRating = () => {
        const { cardTheme, onProductClick, productUrl, rating, reviewCount } = this.props;

        const config = {
            anchor: {
                href: productUrl,
                onClick: (event) => {
                    onProductClick(event, 'reviews');
                },
            },
            rating: {
                automationId: 'productCard-automation-rating',
                averageRating: rating,
                className: cardTheme,
                ratingIconSize: 'small',
                totalRatings:
                    typeof reviewCount === 'string' ? parseInt(reviewCount, 10) : reviewCount,
                wrapperClassName: 'mt-half',
            },
        };

        return productUrl ? (
            <a {...config.anchor}>
                <Rating {...config.rating} />
            </a>
        ) : (
            <Rating {...config.rating} />
        );
    };

    renderCard = () => {
        const {
            handleSwatchDesktopEvent,
            handleSwatchMobileEvent,
            props,
            props: {
                cardTheme,
                cardType,
                ecoRebate,
                enableMiniPDP,
                enableProductTitlePrefix,
                featureFlags: { enableAddToCartForBOPIS, hideBottomBadge, hideTopLeftBadge } = {},
                index,
                isAddToCartClick,
                isCriteoProduct,
                isEnsembledProduct,
                onAddToCartClick,
                ppId,
                pricingConfig,
                productUrl,
                showPricing,
                showSaveForlater,
                skuId,
                skuSwatch,
                storeAvailabilityFilter,
                title,
                upperBadge: { text = '' } = {},
            },
            setGalleryScrollInLocalStorage,
            state: { activeSwatchImageId, playVideo, productCardWidth, swatchSkuId },
        } = this;

        const propsClone = { ...props };
        const titlePrefix =
            enableProductTitlePrefix && title && text && text.toLowerCase().indexOf('new') > -1
                ? 'New!'
                : '';

        if (swatchSkuId)
            propsClone.productUrl = Utils.updateQueryStringParameter(
                productUrl,
                'selectedSKUId',
                swatchSkuId
            );

        // Adding a check to stop sending skuId to quickAddToCart as there was some issue with pre-selection of options in mini-PDP.
        const quickAddToCartSkuId = cardTheme === productCardConfig.cardTheme.GALLERY ? '' : skuId;
        const saveForLaterDisplay = () => {
            if (showSaveForlater) {
                const isFirstProduct = index === 1;
                return (
                    <FeatureFlag name="enableSaveForLater">
                        <div className={cx('saveForLaterContainer')}>
                            <SaveForLaterConnected
                                cardType={cardType}
                                isFirstProduct={isFirstProduct}
                                ppId={ppId}
                                skuId={skuId}
                            />
                        </div>
                    </FeatureFlag>
                );
            }
            return '';
        };
        const { disableFPACAPI } = pricingConfig;
        const enableNewPriceFormat = disableFPACAPI === false;

        const isStoreAvailable = !!(
            storeAvailabilityFilter.length &&
            storeAvailabilityFilter[0].facetValueList &&
            storeAvailabilityFilter[0].facetValueList.length
        );

        const facetValueList = isStoreAvailable ? storeAvailabilityFilter[0].facetValueList : [];

        /* returns if SDPU filter is enabled in gallery */
        let { selected } =
            facetValueList.find(
                (facetConfig) => facetConfig.facetValId === 832 && !facetConfig.greyOut
            ) || {};

        /* should always return true is flag is enabled */
        if (enableAddToCartForBOPIS) {
            selected = false;
        }

        return (
            <div
                className={cx('container', cardType, {
                    miniPDPEnabled: enableMiniPDP,
                })}
            >
                <div className={cx('imgBlock', { catridgeImgBlock: enableNewPriceFormat })}>
                    {/* With the enablePrice Feature Flag 'catridgeImgBlock' have to append */}
                    {this.getProductVideo({ renderVideo: true })}
                    {!hideTopLeftBadge && this.getProductBadge()}
                    <div className={cx('imageDetails', playVideo && 'hideImage')}>
                        {this.renderProductSliding()}
                        {this.renderProductImage()}
                        {this.getProductVideo()}
                        {!hideBottomBadge && this.getExtendedSize()}
                        <QuickAddToCartConnected
                            ppId={ppId}
                            skuId={quickAddToCartSkuId}
                            automationId="QuickAddToCart"
                            themeConfig={{
                                buttonCustomClass: cx('quickAddToCart', 'iconButtonCart'),
                                wrapperClass: cx('quickAddToCartWrapper', 'iconCartWrapper'),
                            }}
                            location={cardTheme}
                            url={
                                swatchSkuId
                                    ? Utils.updateQueryStringParameter(
                                          productUrl,
                                          'selectedSKUId',
                                          swatchSkuId
                                      )
                                    : productUrl
                            }
                            iconStyle
                            enableMiniPDP={enableMiniPDP && !selected}
                            onAddToCartClick={onAddToCartClick}
                            isAddToCartClick={isAddToCartClick}
                        />
                    </div>
                    {saveForLaterDisplay()}
                </div>
                {skuSwatch && skuSwatch.length ? (
                    // TODO: MNPDPYODA-12118 removed lazyload - this needs to be refactored
                    <PointCross lazyLoadComponent {...this.pointCrossOffsetProps}>
                        <Swatches
                            {...{
                                activeSwatchImageId,
                                handleSwatchDesktopEvent,
                                handleSwatchMobileEvent,
                                isEnsembledProduct,
                                productCardWidth,
                                productUrl,
                                setGalleryScrollInLocalStorage,
                                skuSwatch,
                            }}
                        />
                    </PointCross>
                ) : null}
                <div
                    className={cx('detailsWrapper', {
                        newPriceDetailsWrapper: enableNewPriceFormat,
                    })}
                    onFocus={this.resetSelectionForEnsembleHover}
                    onMouseOver={this.resetSelectionForEnsembleHover}
                >
                    <div className={cx('productdetailedBlock')}>
                        <ProductTitle {...{ ...propsClone, prefix: titlePrefix }} />
                        {!hideBottomBadge && this.getExtendedSize({ inDescription: true })}
                        {showPricing && <ProductPrice {...propsClone} />}
                        {ecoRebate ? <EcoRebates skuId={skuId} /> : null}
                        {isCriteoProduct ? (
                            <div className={cx('sponsored')}>Sponsored Product</div>
                        ) : null}
                        {this.renderRating()}
                        <ProductAvailabilityConnected {...propsClone} />
                    </div>
                    {propsClone.children}
                </div>
            </div>
        );
    };

    render = () => {
        const {
            customClass,
            cardTheme,
            cardCustomClass,
            deviceType: { isDesktop },
            shallowCard,
            showPaneLoader,
            isCouponRecommendationsModal,
        } = this.props;
        // part of div updated to data-automation-id to address this warning MNPDPYODA - 10602 - `automationId` prop on a DOM element. If you intentionally want it to appear in the DOM as a custom attribute, spell it as lowercase `automationid` instead. If you accidentally passed it from a parent component, remove it from the DOM element.
        return (
            <div
                className={cx('productCard', customClass, cardTheme, {
                    couponRecommendations: isCouponRecommendationsModal,
                })}
                ref={this.productCardRef}
            >
                <div
                    className={cx('card', 'cardWrapper', cardCustomClass, {
                        cardMinWidth: isDesktop,
                    })}
                    data-automation-id="productCard"
                >
                    {shallowCard && showPaneLoader ? (
                        <div className={cx('col')}>
                            <div className={cx('row')}>
                                <div className={cx('imageTileLoader')} />
                            </div>
                            <div className={cx('row')}>
                                <div className={cx('loader', 'descLoader')} />
                                <div className={cx('loader', 'descLoader')} />
                                <div className={cx('loader', 'descLoader')} />
                            </div>
                        </div>
                    ) : (
                        this.renderCard()
                    )}
                </div>
            </div>
        );
    };
}

const mapStateToProps = ({
    context: { deviceType = { isDesktop: true }, featureFlags = {}, preferences = {} } = {},
    filters: { storeAvailabilityFilter = [] } = {},
    productList = { products: [] },
}) => ({
    deviceType,
    featureFlags,
    preferences,
    productList,
    storeAvailabilityFilter,
});

export default connect(mapStateToProps)(ProductCard);
